/**
 * 4-tile KPI row for the Properties page. Mirrors the design's `.kpi-row`:
 * Properties (count + status breakdown) · Portfolio Value (sum of ARVs) ·
 * Monthly Cash Flow (sum across active properties, signed) · Avg Cap Rate.
 */
import { BarChart3, Building2, DollarSign, Wallet } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { PortfolioKpis } from "./property-metrics";

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(value)).toLocaleString();
}

function fmtPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

interface PropertiesKpiRowProps {
  kpis: PortfolioKpis;
}

export function PropertiesKpiRow({ kpis }: PropertiesKpiRowProps) {
  const cfTone = kpis.totalMonthlyCashFlow >= 0 ? "text-success" : "text-destructive";

  const tiles: Array<{
    label: string;
    icon: typeof Building2;
    value: string;
    valueClass?: string;
    sub: string;
  }> = [
    {
      label: "Properties",
      icon: Building2,
      value: String(kpis.count),
      sub: `${kpis.buckets.in_rehab} in rehab · ${kpis.buckets.owned} owned · ${kpis.buckets.sold} sold`,
    },
    {
      label: "Portfolio Value",
      icon: DollarSign,
      value: fmtCurrency(kpis.totalValue),
      sub: "Sum of ARVs across portfolio",
    },
    {
      label: "Monthly Cash Flow",
      icon: Wallet,
      value: fmtCurrency(kpis.totalMonthlyCashFlow),
      valueClass: cfTone,
      sub: `${fmtCurrency(kpis.totalMonthlyCashFlow * 12)}/yr · active rentals only`,
    },
    {
      label: "Avg Cap Rate",
      icon: BarChart3,
      value: kpis.capRateSampleCount > 0 ? fmtPct(kpis.avgCapRate) : "—",
      sub: `${kpis.capRateSampleCount} ${
        kpis.capRateSampleCount === 1 ? "property" : "properties"
      } contributing`,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.label} className="shadow-soft">
          <CardContent className="flex flex-col gap-1.5 p-4">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <tile.icon className="h-3.5 w-3.5 text-primary" aria-hidden />
              {tile.label}
            </span>
            <span
              className={cn(
                "text-2xl font-extrabold leading-tight tabular-nums",
                tile.valueClass ?? "text-foreground",
              )}
            >
              {tile.value}
            </span>
            <span className="text-xs text-muted-foreground">{tile.sub}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
